/**
 * ExportRenderer - Orchestrates the export rendering process
 * 
 * Handles:
 * - Rendering photos using RenderService (max 5 concurrent)
 * - Progress tracking per photo
 * - Storing rendered blobs in ExportCacheManager
 * - Executing export strategy for each completed photo
 * - Error handling and retries (max 3 attempts)
 * - Updating queue item status on server
 */

import RenderService from './render_service.js';
import ExportCacheManager from './export_cache_manager.js';
import { getExportStrategy } from './export_strategies.js';

export class ExportRenderer {
  constructor(editorState, exportQueueManager) {
    this.editorState = editorState;
    this.exportQueueManager = exportQueueManager;
    this.exportCacheManager = ExportCacheManager;
    this.renderService = RenderService;
    
    this.maxConcurrent = 5;
    this.maxRetries = 3;
    this.activeRenders = new Map(); // photo_id → { cancel, attempts }
    this.strategy = null;
  }

  /**
   * Start export process
   */
  async startExport() {
    console.log('[ExportRenderer] === START EXPORT ===');
    const state = this.editorState.get();
    
    // Get pending items
    const pendingItems = state.exportQueueItems.filter(item => item.status === 'pending');
    if (pendingItems.length === 0) {
      console.log('[ExportRenderer] No pending items to export');
      return;
    }
    
    console.log(`[ExportRenderer] Starting export of ${pendingItems.length} photos`);
    console.log('[ExportRenderer] Pending item IDs:', pendingItems.map(i => i.id));
    
    // Set exporting flag
    console.log('[ExportRenderer] Setting isExporting = true');
    this.editorState.set({ isExporting: true });
    
    try {
      // Initialize strategy
      const strategyName = state.exportSaveLocation || 'zip';
      console.log(`[ExportRenderer] Using strategy: ${strategyName}`);
      this.strategy = getExportStrategy(strategyName, this.editorState);
      
      // Check availability
      console.log('[ExportRenderer] Checking strategy availability...');
      const { available, reason } = await this.strategy.checkAvailability();
      if (!available) {
        throw new Error(`Export strategy not available: ${reason}`);
      }
      
      // Initialize strategy (e.g., load JSZip, select folder)
      console.log('[ExportRenderer] Initializing strategy...');
      await this.strategy.initialize();
      console.log('[ExportRenderer] Strategy initialized');
      
      // Clear export cache before starting
      console.log('[ExportRenderer] Clearing export cache...');
      await this.exportCacheManager.clearAll();
      
      // Process items in batches of maxConcurrent
      console.log('[ExportRenderer] Processing queue...');
      await this.processQueue(pendingItems);
      console.log('[ExportRenderer] Queue processing complete');
      
      // Finalize strategy (e.g., generate zip file)
      console.log('[ExportRenderer] Finalizing strategy...');
      await this.strategy.finalize();
      console.log('[ExportRenderer] Strategy finalized');
      
      // Clear export cache after completion
      console.log('[ExportRenderer] Clearing export cache (final)...');
      await this.exportCacheManager.clearAll();
      
      console.log('[ExportRenderer] === EXPORT COMPLETED SUCCESSFULLY ===');
      
    } catch (error) {
      console.error('[ExportRenderer] === EXPORT FAILED ===', error);
      throw error;
    } finally {
      // Clear exporting flag
      console.log('[ExportRenderer] Setting isExporting = false (finally block)');
      this.editorState.set({ isExporting: false });
      this.activeRenders.clear();
      console.log('[ExportRenderer] === FINALLY BLOCK COMPLETE ===');
    }
  }

  /**
   * Cancel export process
   */
  cancelExport() {
    console.log('[ExportRenderer] Cancelling export...');
    
    // Cancel all active renders
    this.activeRenders.forEach(({ cancel }) => {
      if (cancel) cancel();
    });
    
    this.activeRenders.clear();
    this.editorState.set({ isExporting: false, activeExports: new Map() });
  }

  /**
   * Process queue items in batches
   * @param {array} items - Queue items to process
   */
  async processQueue(items) {
    console.log(`[ExportRenderer] processQueue: ${items.length} total items`);
    
    // Process items in batches of maxConcurrent using Promise.allSettled
    for (let i = 0; i < items.length; i += this.maxConcurrent) {
      const batch = items.slice(i, i + this.maxConcurrent);
      console.log(`[ExportRenderer] Processing batch: ${batch.length} items (${i + 1}-${i + batch.length} of ${items.length})`);
      
      // Process all items in batch concurrently
      await Promise.allSettled(batch.map(item => this.processItem(item)));
      
      console.log(`[ExportRenderer] Batch complete`);
    }
    
    console.log(`[ExportRenderer] processQueue complete!`);
  }

  /**
   * Process a single queue item (render + store + export)
   * @param {object} item - Export queue item
   * @returns {Promise} Promise that resolves when item is fully processed
   */
  async processItem(item) {
    const photoId = item.photo_id;
    let attempts = 0;
    
    console.log(`[ExportRenderer] >>> processItem START for photo ${photoId}, item ${item.id}`);
    
    while (attempts < this.maxRetries) {
      try {
        attempts++;
        
        console.log(`[ExportRenderer] Processing photo ${photoId} (attempt ${attempts}/${this.maxRetries})`);
        
        // Update item status to rendering
        console.log(`[ExportRenderer] Updating status to 'rendering' for item ${item.id}`);
        await this.exportQueueManager.updateStatus(item.id, 'rendering');
        console.log(`[ExportRenderer] Status updated to 'rendering'`);
        
        this.updatePhotoProgress(photoId, 0, 'rendering');
        
        // Render photo
        console.log(`[ExportRenderer] Starting render for photo ${photoId}`);
        const blob = await this.renderPhoto(item);
        console.log(`[ExportRenderer] Render complete for photo ${photoId}, blob size: ${blob.size}`);
        
        // Store in export cache
        console.log(`[ExportRenderer] Storing in export cache...`);
        await this.exportCacheManager.store(photoId, blob, item.photo.original_filename);
        
        // Compute export filename
        const exportFilename = this.strategy.applyFilenamePattern(
          item.filename_pattern,
          item.photo
        );
        console.log(`[ExportRenderer] Export filename: ${exportFilename}`);
        
        // Execute export strategy
        console.log(`[ExportRenderer] Executing strategy.processPhoto...`);
        await this.strategy.processPhoto(item, blob, exportFilename);
        console.log(`[ExportRenderer] Strategy.processPhoto complete`);
        
        // Delete from export cache (no longer needed)
        await this.exportCacheManager.delete(photoId);
        
        // Update item status to completed
        console.log(`[ExportRenderer] Updating status to 'completed' for item ${item.id}`);
        await this.exportQueueManager.updateStatus(item.id, 'completed');
        console.log(`[ExportRenderer] Status updated to 'completed'`);
        
        this.updatePhotoProgress(photoId, 100, 'completed');
        
        console.log(`[ExportRenderer] <<< processItem COMPLETE for photo ${photoId}`);
        return Promise.resolve();
        
      } catch (error) {
        console.error(`[ExportRenderer] Error processing photo ${photoId} (attempt ${attempts}):`, error);
        
        if (attempts >= this.maxRetries) {
          // Max retries reached, mark as failed
          await this.exportQueueManager.updateStatus(item.id, 'failed', error.message);
          this.updatePhotoProgress(photoId, 0, 'failed', error.message);
          return Promise.resolve(); // Continue with other photos
        }
        
        // Retry after delay
        await new Promise(resolve => setTimeout(resolve, 1000 * attempts));
      }
    }
  }

  /**
   * Render a photo using RenderService
   * @param {object} item - Export queue item
   * @returns {Promise<Blob>} Rendered image blob
   */
  async renderPhoto(item) {
    const photoId = item.photo_id;
    const settings = item.settings;
    
    return new Promise((resolve, reject) => {
      let finalBlob = null;
      
      const cancel = this.renderService.getPhoto(
        photoId,
        {
          style: settings.style || 'hta',
          resolution: settings.resolution || 1024,
          numInferenceSteps: settings.num_inference_steps || 28,
          style_strength: settings.style_strength || 1.0,
          controlScale: settings.control_scale || 1.0,
          seed: settings.seed || 42,
          baseShift: settings.base_shift || 0.5,
          maxShift: settings.max_shift || 1.15,
          sampleEvery: settings.sample_every || 0, // 0 = only final image
          jpgQuality: settings.jpg_quality || 85,
          ...settings
        },
        {
          onProgress: (imageData) => {
            // Update progress (intermediate frames)
            const blob = new Blob([imageData], { type: 'image/jpeg' });
            finalBlob = blob;
            
            // Estimate progress (rough estimate based on data size)
            const progress = Math.min(95, Math.floor((imageData.length / (1024 * 200)) * 100));
            this.updatePhotoProgress(photoId, progress, 'rendering');
          },
          onComplete: (imageData) => {
            // Final image
            const blob = new Blob([imageData], { type: 'image/jpeg' });
            finalBlob = blob;
            this.updatePhotoProgress(photoId, 100, 'rendering');
            resolve(blob);
          },
          onError: (error) => {
            reject(error);
          }
        }
      );
      
      // Store cancel function
      this.activeRenders.set(photoId, { cancel, attempts: 1 });
    });
  }

  /**
   * Update photo progress in state
   * @param {number} photoId - Photo ID
   * @param {number} progress - Progress percentage (0-100)
   * @param {string} status - Status ('rendering'|'completed'|'failed')
   * @param {string} error - Error message if failed
   */
  updatePhotoProgress(photoId, progress, status, error = null) {
    const state = this.editorState.get();
    const activeExports = new Map(state.activeExports);
    
    activeExports.set(photoId, {
      progress,
      status,
      error
    });
    
    this.editorState.set({ activeExports });
  }
}

